/**
 * Feuille de brouillon — le tableau à remplir (moteur v42 étendu, lots 6.21 à 6.23) :
 * tableau des effectifs (et effectifs cumulés) d'une série, tableau de proportionnalité,
 * tableau à double entrée, tableau de valeurs, tableau des fréquences.
 *
 * Une ligne « tableau » est UN bout (« ⊟{…} », `@jeraisonne/reasoner`) : l'élève le remplit
 * dans l'éditeur (les cases données de l'énoncé sont verrouillées, les autres se tapent au
 * pavé), puis il est posé seul sur la ligne et jugé case par case par le moteur. Toucher
 * le tableau de la ligne en cours le rouvre, la case fausse en rouge.
 *
 * `GridTableView` : le tableau en lecture (énoncé, feuille, aperçu) ; `GridTableEditor` :
 * l'éditeur plein écran ; `GridTableHost` : l'éditeur ouvert pour une feuille.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Delete, X } from 'lucide-react';
import {
  commit,
  createState,
  gridTableSkeleton,
  gridText,
  isGridText,
  judge,
  parseGrid,
  type Grid,
  type GridSkeleton,
} from '@jeraisonne/reasoner';
import { engineChips, isFrac, type LineItem } from './line-items';
import type { ScratchSession } from './scratch-session';
import { vibrate } from './use-long-press';
import './grid-table.css';

/** Le tableau d'une ligne (un seul bout « tableau à remplir ») ; null sinon. */
export function gridOfItems(items: LineItem[]): { text: string; grid: Grid } | null {
  if (items.length !== 1) return null;
  const it = items[0]!;
  if (isFrac(it) || it.atoms.length !== 1 || !isGridText(it.atoms[0]!)) return null;
  const grid = parseGrid(it.atoms[0]!);
  return grid ? { text: it.atoms[0]!, grid } : null;
}

/** Le nom d'un tableau, lu sur ses lignes (aperçu, feuille relue). */
export function gridTableTitle(grid: Grid): string {
  if (grid.rows.some((r) => /fréquence|pourcentage|%/i.test(r.label)))
    return 'Tableau des fréquences';
  if (grid.head) return 'Tableau à double entrée';
  if (/^valeur/i.test(grid.rows[0]?.label ?? '')) return 'Tableau des effectifs';
  return 'Tableau de proportionnalité';
}

/** Écriture d'une case : « − » typographique, espaces fines retirées. */
function cellText(c: string): string {
  return c.replace(/-/g, '−');
}

interface Bad {
  row: number;
  col?: number;
}

function columns(n: number, compact = false): string {
  const cell = compact ? '1.75rem' : '2.75rem';
  return `minmax(${compact ? '2.5rem' : '3.5rem'}, max-content) repeat(${n}, minmax(${cell}, max-content))`;
}

/** Le tableau en lecture : énoncé (« ? » à trouver), ligne de la feuille, aperçu. */
export function GridTableView({
  grid,
  bad,
  compact,
  label,
}: {
  grid: Grid;
  bad?: Bad;
  compact?: boolean;
  label?: string;
}) {
  const n = Math.max(grid.head?.length ?? 0, ...grid.rows.map((r) => r.cells.length));
  return (
    <div
      className="sc-gt"
      data-compact={compact ? '' : undefined}
      role="table"
      aria-label={label ?? gridTableTitle(grid)}
      style={{ gridTemplateColumns: columns(n, compact) }}
    >
      {grid.head && (
        <div className="contents" role="row">
          <div className="sc-gt-corner" role="columnheader" aria-label="" />
          {grid.head.map((h, j) => (
            <div key={j} className="sc-gt-head" role="columnheader">
              {h}
            </div>
          ))}
        </div>
      )}
      {grid.rows.map((r, ri) => (
        <div key={ri} className="contents" role="row">
          <div
            className="sc-gt-label"
            role="rowheader"
            data-bad={bad?.row === ri && bad.col === undefined ? '' : undefined}
          >
            {r.label}
          </div>
          {r.cells.map((c, j) => (
            <div
              key={j}
              className="sc-gt-cell"
              role="cell"
              data-unknown={c.trim() === '?' ? '' : undefined}
              data-bad={bad?.row === ri && bad.col === j ? '' : undefined}
            >
              {cellText(c)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ éditeur ------------------------------ */

/** le pavé, ligne par ligne (4 colonnes) */
const PAD = ['7', '8', '9', '⌫', '4', '5', '6', '/', '1', '2', '3', '−', '0', ',', 'next'] as const;
const KEY_LABEL: Record<string, string> = {
  '⌫': 'Effacer un chiffre',
  '/': 'Barre de fraction',
  '−': 'Signe moins',
  ',': 'Virgule',
};

/** une touche du pavé sur la case : garde une écriture lisible (« −3 », « 2,5 », « 7/3 ») */
export function typeKey(cur: string, key: string): string {
  if (cur.length >= 12) return cur;
  const part = cur.split('/').pop() ?? '';
  if (key === '−') return cur === '' ? '−' : cur.endsWith('/') ? cur + '−' : cur;
  if (key === ',') return /\d$/.test(part) && !part.includes(',') ? cur + ',' : cur;
  if (key === '/') return /\d$/.test(cur) && !cur.includes('/') ? cur + '/' : cur;
  return cur + key;
}

type Cell = { given: string | null; value: string };

function startCells(skeleton: GridSkeleton, initial: Grid | null): Cell[][] {
  const fits =
    initial &&
    initial.rows.length === skeleton.rows.length &&
    initial.rows.every(
      (r, i) =>
        r.label === skeleton.rows[i]!.label && r.cells.length === skeleton.rows[i]!.cells.length,
    );
  return skeleton.rows.map((r, i) =>
    r.cells.map((g, j) => ({
      given: g,
      value: g ?? (fits ? cellText(initial!.rows[i]!.cells[j]!) : ''),
    })),
  );
}

/** les cases à remplir, dans l'ordre de lecture */
function editable(cells: Cell[][]): { r: number; c: number }[] {
  return cells.flatMap((row, r) =>
    row.flatMap((cell, c) => (cell.given === null ? [{ r, c }] : [])),
  );
}

const KIND_HELP: Record<GridSkeleton['kind'], string> = {
  series:
    'Range les valeurs de la plus petite à la plus grande, écris l’effectif de chacune, puis les effectifs cumulés.',
  proportion:
    'Complète les cases vides. S’il y a une ligne de quotients : dans chaque colonne, la 2e ligne divisée par la 1re.',
  cross: 'Complète les cases : chaque ligne et chaque colonne s’additionne dans sa case « Total ».',
  values:
    'Pour chaque colonne, remplace la variable par sa valeur dans la formule de la ligne, puis calcule.',
  freq: 'Dans chaque colonne : fréquence = effectif ÷ effectif total (en fraction si elle ne tombe pas juste ; × 100 pour un pourcentage). Une colonne « Total » additionne les autres.',
};
const RESIDUE_HELP =
  'Pour chaque reste de n, calcule l’expression, puis son reste dans la division : un entier entre 0 et m − 1.';

/** l'aide de l'éditeur, selon le tableau */
export function gridHelp(skeleton: GridSkeleton): string {
  if (skeleton.kind === 'values' && skeleton.title === 'Tableau de restes') return RESIDUE_HELP;
  return KIND_HELP[skeleton.kind];
}

export function GridTableEditor({
  skeleton,
  statement,
  initial,
  bad,
  onCancel,
  onSubmit,
}: {
  skeleton: GridSkeleton;
  /** le tableau de l'énoncé (une série brute : les valeurs à ranger) */
  statement?: Grid | null;
  initial?: Grid | null;
  bad?: Bad;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const [cells, setCells] = useState<Cell[][]>(() => startCells(skeleton, initial ?? null));
  const order = useMemo(() => editable(cells), [cells]);
  const [sel, setSel] = useState<{ r: number; c: number } | null>(() => {
    const all = editable(startCells(skeleton, initial ?? null));
    if (bad && bad.col !== undefined && all.some((p) => p.r === bad.row && p.c === bad.col))
      return { r: bad.row, c: bad.col };
    const start = startCells(skeleton, initial ?? null);
    return all.find((p) => !start[p.r]![p.c]!.value) ?? all[0] ?? null;
  });
  const dialogRef = useRef<HTMLElement>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  const complete = order.every((p) => cells[p.r]![p.c]!.value.trim() !== '');
  const grid: Grid = {
    ...(skeleton.head ? { head: skeleton.head } : {}),
    rows: skeleton.rows.map((r, i) => ({
      label: r.label,
      cells: cells[i]!.map((c) => c.value.trim()),
    })),
  };

  const setValue = (r: number, c: number, f: (v: string) => string) =>
    setCells((cs) =>
      cs.map((row, ri) =>
        ri !== r
          ? row
          : row.map((cell, ci) => (ci !== c ? cell : { ...cell, value: f(cell.value) })),
      ),
    );
  const press = (key: string) => {
    if (!sel) return;
    vibrate(6);
    if (key === '⌫') setValue(sel.r, sel.c, (v) => v.slice(0, -1));
    else setValue(sel.r, sel.c, (v) => typeKey(v, key));
  };
  const next = () => {
    if (!order.length) return;
    vibrate(6);
    const i = sel ? order.findIndex((p) => p.r === sel.r && p.c === sel.c) : -1;
    setSel(order[(i + 1) % order.length]!);
  };
  // la case choisie (la case fausse, à la reprise) reste en vue dans un tableau large
  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tableRef.current
      ?.querySelector<HTMLElement>('[data-picked]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [sel]);
  const pressRef = useRef(press);
  pressRef.current = press;
  const nextRef = useRef(next);
  nextRef.current = next;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const back = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        cancelRef.current();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = /^\d$/.test(e.key)
        ? e.key
        : e.key === '-'
          ? '−'
          : e.key === '.' || e.key === ','
            ? ','
            : e.key === '/'
              ? '/'
              : e.key === 'Backspace'
                ? '⌫'
                : null;
      if (k) {
        e.preventDefault();
        pressRef.current(k);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        nextRef.current();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey, true);
      back?.focus?.();
    };
  }, []);

  const n = Math.max(skeleton.head?.length ?? 0, ...skeleton.rows.map((r) => r.cells.length));
  const where = (r: number, c: number) =>
    skeleton.head
      ? `${skeleton.rows[r]!.label}, ${skeleton.head[c]}`
      : `${skeleton.rows[r]!.label}, colonne ${c + 1}`;

  return createPortal(
    <div className="sc-root fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-4">
      <div className="sc-scrim absolute inset-0" onClick={onCancel} aria-hidden />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="grid-table-title"
        tabIndex={-1}
        data-testid="grid-table-editor"
        className="sc-sheet native:max-h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] relative flex max-h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-3xl outline-none md:w-[92vw] md:max-w-[760px] md:rounded-3xl"
      >
        <header className="flex flex-none items-start gap-2 pb-1 pl-4 pr-2 pt-3 md:pl-5">
          <div className="min-w-0 flex-1">
            <h2 id="grid-table-title" className="font-display text-[17px] font-bold md:text-lg">
              {skeleton.title}
            </h2>
            <p className="mt-0.5 text-sm text-[var(--sc-ink-2)]">{gridHelp(skeleton)}</p>
          </div>
          <button
            type="button"
            className="sc-icon-btn"
            aria-label="Fermer le tableau sans le poser"
            onClick={onCancel}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 md:px-5">
          {statement && (
            <>
              <p className="mt-2 text-sm font-semibold text-[var(--sc-ink)]">L’énoncé</p>
              <div className="sc-gt-wrap mt-1" data-testid="grid-table-statement">
                <GridTableView grid={statement} compact label="Tableau de l’énoncé" />
              </div>
            </>
          )}
          <p className="mt-3 text-sm font-semibold text-[var(--sc-ink)]">Ton tableau</p>
          <p className="text-xs text-[var(--sc-muted)]">
            Touche une case, puis écris son nombre au pavé : un entier, un décimal « 2,5 » ou une
            fraction « 7/3 ». Les cases grisées viennent de l’énoncé.
          </p>
          <div className="sc-gt-wrap mt-2 pb-1">
            <div
              ref={tableRef}
              className="sc-gt sc-gt-edit"
              role="table"
              aria-label={`${skeleton.title} à remplir`}
              style={{ gridTemplateColumns: columns(n) }}
            >
              {skeleton.head && (
                <div className="contents" role="row">
                  <div className="sc-gt-corner" role="columnheader" aria-label="" />
                  {skeleton.head.map((h, j) => (
                    <div key={j} className="sc-gt-head" role="columnheader">
                      {h}
                    </div>
                  ))}
                </div>
              )}
              {skeleton.rows.map((r, ri) => (
                <div key={r.label} className="contents" role="row">
                  <div
                    className="sc-gt-label"
                    role="rowheader"
                    data-bad={bad?.row === ri && bad.col === undefined ? '' : undefined}
                  >
                    {r.label}
                  </div>
                  {cells[ri]!.map((cell, c) =>
                    cell.given !== null ? (
                      <div
                        key={c}
                        role="cell"
                        className="sc-gt-cell"
                        data-given=""
                        aria-label={`${where(ri, c)} : ${cellText(cell.given)}, donnée de l’énoncé`}
                      >
                        {cellText(cell.given)}
                      </div>
                    ) : (
                      <button
                        key={c}
                        type="button"
                        role="cell"
                        className="sc-gt-cell sc-gt-tap"
                        data-picked={sel?.r === ri && sel.c === c ? '' : undefined}
                        data-bad={bad?.row === ri && bad.col === c ? '' : undefined}
                        aria-label={`${where(ri, c)} : ${cell.value || 'vide'}`}
                        aria-pressed={sel?.r === ri && sel.c === c}
                        onClick={() => {
                          vibrate(6);
                          setSel({ r: ri, c });
                        }}
                      >
                        {cell.value}
                      </button>
                    ),
                  )}
                </div>
              ))}
            </div>
          </div>

          <div
            className="sc-gt-pad mt-3"
            role="group"
            aria-label="Pavé numérique"
            data-testid="grid-table-pad"
          >
            {PAD.map((k) =>
              k === 'next' ? (
                <button
                  key={k}
                  type="button"
                  className="sc-chip sc-gt-next"
                  data-kind="op"
                  aria-label="Case suivante"
                  disabled={!order.length}
                  onClick={next}
                >
                  <ArrowRight className="h-5 w-5" aria-hidden />
                </button>
              ) : (
                <button
                  key={k}
                  type="button"
                  className="sc-chip"
                  data-kind={/^\d$/.test(k) || k === ',' ? 'num' : 'op'}
                  aria-label={KEY_LABEL[k] ?? `Chiffre ${k}`}
                  disabled={!sel}
                  onClick={() => press(k)}
                >
                  {k === '⌫' ? <Delete className="h-5 w-5" aria-hidden /> : k}
                </button>
              ),
            )}
          </div>
        </div>

        <footer className="flex flex-none flex-col-reverse gap-2 border-t border-[var(--sc-line)] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:justify-end md:px-5">
          <button
            type="button"
            className="sc-mini-btn w-full sm:w-auto"
            data-ghost=""
            onClick={onCancel}
          >
            Annuler
          </button>
          <button
            type="button"
            className="sc-mini-btn w-full sm:w-auto"
            disabled={!complete}
            onClick={() => onSubmit(gridText(grid))}
          >
            Mettre dans ma feuille
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

/* ------------------------------ ouverture ------------------------------ */

interface Request {
  session: ScratchSession;
  /** le tableau de la ligne en cours, à reprendre */
  initial: string | null;
}
let request: Request | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());

export function openGridTableEditor(session: ScratchSession, initial: string | null = null): void {
  request = { session, initial };
  emit();
}
export function closeGridTableEditor(): void {
  request = null;
  emit();
}
function subscribe(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** Ce que l'éditeur propose : rejoue les lignes de la feuille sur un état neuf du moteur. */
export function gridSkeletonOf(session: ScratchSession): GridSkeleton | null {
  const st = createState(session.model);
  for (const l of session.getSnapshot().lines) {
    try {
      const v = judge(st, engineChips(l.items), { verify: true });
      if (v.kind === 'step' || v.kind === 'ready' || v.kind === 'useless') commit(st, v);
    } catch {
      /* ligne que le moteur ne relit plus */
    }
  }
  return gridTableSkeleton(st);
}

/** L'éditeur ouvert pour cette feuille (monté par la palette). */
export function GridTableHost({ session }: { session: ScratchSession }) {
  const req = useSyncExternalStore(subscribe, () =>
    request?.session === session ? request : null,
  );
  const skeleton = useMemo(() => (req ? gridSkeletonOf(session) : null), [req, session]);
  if (!req || !skeleton) return null;
  const start = parseGrid(session.model.kernel?.start ?? '');
  // une série brute : rien n'est donné dans le tableau, l'énoncé reste sous les yeux
  const statement = skeleton.rows.every((r) => r.cells.every((c) => c === null)) ? start : null;
  const initial = req.initial ? parseGrid(req.initial) : null;
  const v = session.getSnapshot().verdict;
  const bad = req.initial && v.atoms[0] === req.initial ? v.cell : undefined;
  return (
    <GridTableEditor
      skeleton={skeleton}
      statement={statement}
      initial={initial}
      bad={bad}
      onCancel={closeGridTableEditor}
      onSubmit={(text) => {
        closeGridTableEditor();
        session.clearLine();
        session.addChip({ atoms: [text], text: skeleton.title });
      }}
    />
  );
}
